import { z } from "zod"
import qs from "qs"
import { useEffect, useMemo, useReducer, useRef, useState, type Dispatch, type FormEvent, type ReactNode } from "react"
import {
  Form,
  useActionData,
  useNavigation,
  useRouteLoaderData,
  useLoaderData,
  useNavigate,
  useParams,
  redirect,
  type LoaderFunctionArgs,
} from "react-router"
import { ChevronRightIcon, EyeIcon, PlusIcon, TrashIcon } from "lucide-react"
import { fetchAPI, handleResponse, throwIfNotOk, cn, ApiRequestError, type APIError } from "@/utils"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { Button, buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import { Checks, type Check } from "./Checks"

export interface RootCauseDraft {
  id: string
  name: string
  value: string
}

export interface CheckDraft {
  id: string
  reference: string
  isExistingReference: boolean
  questionName: string
  guidanceName: string
  project: string
  product: string
  group: string
  level: string
  order: string
  outcomeLookup: string
  qualityLookup: string
  ignoreOutcome: boolean
  rootCauses: RootCauseDraft[]
  checks: CheckDraft[]
}

interface QuestionOption {
  organisation: string
  name: string
  value: string
  code: string
  active: boolean
}

interface GuidanceOption {
  organisation: string
  name: string
  code: string
  value: string
  active: boolean
}

interface RootCauseOption {
  organisation: string
  code: string
  name: string
  value: string
  active: boolean
}

type CascadeField = "project" | "product" | "group" | "outcomeLookup" | "qualityLookup"
const CASCADE_FIELDS: readonly CascadeField[] = ["project", "product", "group", "outcomeLookup", "qualityLookup"]

function isCascadeField(field: string): field is CascadeField {
  return (CASCADE_FIELDS as readonly string[]).includes(field)
}

type PreviewKind = "question" | "guidance" | "outcomeLookup" | "qualityLookup" | null

const byteStringPattern = /^-?(?:0|[1-9]\d*)$/

const rootCauseSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
})

const byteField = (label: string) =>
  z
    .string()
    .min(1, `${label} is required`)
    .refine(
      (v) => byteStringPattern.test(v),
      `${label} must be a whole number (e.g. 1, 2, 10) — no decimals or letters`,
    )
    .refine((v) => Number(v) >= 1 && Number(v) <= 255, `${label} must be between 1 and 255`)

const rootCausesArraySchema = z.array(rootCauseSchema).superRefine((rootCauses, ctx) => {
  const seenAtIndex = new Map<string, number>()
  rootCauses.forEach((rc, index) => {
    if (rc.name.length === 0) return
    const firstIndex = seenAtIndex.get(rc.name)
    if (firstIndex != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Root cause "${rc.name}" is added more than once — remove the duplicate`,
        path: [index, "name"],
      })
    } else {
      seenAtIndex.set(rc.name, index)
    }
  })
})

export const checkSchema: z.ZodType<{
  reference: string
  questionName: string
  guidanceName: string
  project: string
  product: string
  group: string
  level: string
  order: string
  outcomeLookup: string
  qualityLookup: string
  ignoreOutcome: boolean
  rootCauses: RootCauseDraft[]
  checks: unknown[]
}> = z.lazy(() =>
  z
    .object({
      reference: z.string(),
      questionName: z.string().trim().min(1, "Question is required"),
      guidanceName: z.string(),
      project: z.string(),
      product: z.string(),
      group: z.string().trim().min(1, "Group / Theme is required"),
      level: z
        .string()
        .refine(
          (value) => ["1", "2", "3"].includes(value),
          "Choose a check type: a check with sub-checks (level 1) or a standalone check (level 3)",
        ),
      order: byteField("Order"),
      outcomeLookup: z.string(),
      qualityLookup: z.string(),
      ignoreOutcome: z.boolean(),
      rootCauses: rootCausesArraySchema,
      checks: z.array(checkSchema),
    })
    .superRefine((check, ctx) => {
      if (check.level === "1" && check.checks.length === 0) {
        ctx.addIssue({ code: "custom", path: ["checks"], message: "A level 1 check must have at least one sub-check" })
      }

      if ((check.level === "2" || check.level === "3") && check.checks.length > 0) {
        ctx.addIssue({
          code: "custom",
          path: ["checks"],
          message: `A level ${check.level} check cannot have sub-checks`,
        })
      }

      if (check.level === "1") {
        check.checks.forEach((child, index) => {
          if (child.level !== "2") {
            ctx.addIssue({
              code: "custom",
              path: ["checks", index, "level"],
              message: "Children of a level 1 check must be level 2",
            })
          }
        })
      }
    }),
)

const rootCheckSchema = checkSchema.superRefine((check, ctx) => {
  if (check.level === "2") {
    ctx.addIssue({ code: "custom", path: ["level"], message: "A level 2 check must be a child of a level 1 check" })
  }
})

function collectOrderConflict(root: CheckDraft): string | null {
  const seenOrders = new Set<string>()

  function walk(node: CheckDraft): string | null {
    if (node.order.length > 0) {
      if (seenOrders.has(node.order)) {
        return `Order '${node.order}' is already used by another check. Order values must be unique across the check, including sub-checks.`
      }
      seenOrders.add(node.order)
    }

    for (const child of node.checks) {
      const conflict = walk(child)
      if (conflict != null) return conflict
    }
    return null
  }

  return walk(root)
}

export function emptyCheckDraft(
  defaults?: Partial<Pick<CheckDraft, "project" | "product" | "group" | "level" | "outcomeLookup" | "qualityLookup">>,
): CheckDraft {
  return {
    id: crypto.randomUUID(),
    reference: "",
    questionName: "",
    isExistingReference: false,
    guidanceName: "",
    project: defaults?.project ?? "",
    product: defaults?.product ?? "",
    group: defaults?.group ?? "",
    level: defaults?.level ?? "",
    order: "",
    outcomeLookup: defaults?.outcomeLookup ?? "",
    qualityLookup: defaults?.qualityLookup ?? "",
    ignoreOutcome: false,
    rootCauses: [],
    checks: [],
  }
}

type InheritedCheckFields = Pick<CheckDraft, "project" | "product" | "group" | "outcomeLookup" | "qualityLookup">

export function fromApiCheck(check: Check, inherited?: InheritedCheckFields, depth = 0): CheckDraft {
  const inheritedFields: InheritedCheckFields = {
    project: inherited?.project ?? check.project?.name ?? "",
    product: inherited?.product ?? check.product ?? "",
    group: inherited?.group ?? check.group ?? "",
    outcomeLookup: inherited?.outcomeLookup ?? check.outcomeLookup ?? "",
    qualityLookup: inherited?.qualityLookup ?? check.qualityLookup ?? "",
  }

  return {
    id: crypto.randomUUID(),
    reference: check.reference ?? "",
    isExistingReference: (check.reference ?? "").length > 0,
    questionName: check.question?.name ?? "",
    guidanceName: check.guidance?.name ?? "",
    project: inheritedFields.project,
    product: inheritedFields.product,
    group: inheritedFields.group,
    level: depth === 0 ? String(check.level ?? "") : "2",
    order: String(check.order ?? ""),
    outcomeLookup: inheritedFields.outcomeLookup,
    qualityLookup: inheritedFields.qualityLookup,
    ignoreOutcome: check.ignoreOutcome ?? false,
    rootCauses: (check.rootCauses ?? []).map((rc) => ({
      id: crypto.randomUUID(),
      name: rc.name ?? "",
      value: rc.value ?? "",
    })),
    checks: (check.checks ?? []).map((child) => fromApiCheck(child, inheritedFields, depth + 1)),
  }
}

function findCheckByReference(checks: Check[], reference: string): Check | null {
  for (const check of checks) {
    if (check.reference === reference) return check
    if (check.checks != null) {
      const found = findCheckByReference(check.checks, reference)
      if (found != null) return found
    }
  }
  return null
}

function toSaveRequest(draft: CheckDraft): Record<string, unknown> {
  const emptyToUndefined = (v: string) => (v.length > 0 ? v : undefined)
  return {
    reference: emptyToUndefined(draft.reference),
    questionName: draft.questionName,
    guidanceName: emptyToUndefined(draft.guidanceName),
    project: emptyToUndefined(draft.project),
    product: emptyToUndefined(draft.product),
    group: draft.group,
    level: Number(draft.level),
    order: Number(draft.order),
    outcomeLookup: draft.outcomeLookup,
    qualityLookup: draft.qualityLookup,
    ignoreOutcome: draft.ignoreOutcome,
    rootCauses: draft.rootCauses.map(({ name, value }) => ({ name, value })),
    checks: draft.checks.map(toSaveRequest),
  }
}

type Path = number[]
type EditableStringField =
  | "reference"
  | "questionName"
  | "guidanceName"
  | "project"
  | "product"
  | "group"
  | "level"
  | "order"
  | "outcomeLookup"
  | "qualityLookup"

type Action =
  | { type: "SET_STRING_FIELD"; path: Path; field: EditableStringField; value: string }
  | { type: "SET_IGNORE_OUTCOME"; path: Path; value: boolean }
  | { type: "CONFIRM_CASCADE" }
  | { type: "CANCEL_CASCADE" }
  | { type: "ADD_SUBCHECK"; path: Path }
  | { type: "REMOVE_SUBCHECK"; path: Path }
  | { type: "ADD_ROOT_CAUSE"; path: Path }
  | { type: "SET_ROOT_CAUSE_NAME"; path: Path; index: number; name: string; value: string }
  | { type: "SET_ROOT_CAUSE_VALUE"; path: Path; index: number; value: string }
  | { type: "REMOVE_ROOT_CAUSE"; path: Path; index: number }

interface FormState {
  root: CheckDraft
  pendingCascade: { path: Path; field: CascadeField; value: string } | null
}

function getAtPath(root: CheckDraft, path: Path): CheckDraft {
  return path.reduce((node, index) => node.checks[index], root)
}

function getAtPathSafe(root: CheckDraft, path: Path): CheckDraft | null {
  let node = root
  for (const index of path) {
    const next: CheckDraft | undefined = node.checks[index]
    if (next == null) return null
    node = next
  }
  return node
}

function updateAtPath(root: CheckDraft, path: Path, updater: (node: CheckDraft) => CheckDraft): CheckDraft {
  if (path.length === 0) return updater(root)
  const [head, ...rest] = path
  return {
    ...root,
    checks: root.checks.map((child, index) => (index === head ? updateAtPath(child, rest, updater) : child)),
  }
}

function applyFieldToAllDescendants(node: CheckDraft, field: CascadeField, value: string): CheckDraft {
  return {
    ...node,
    checks: node.checks.map((child) => applyFieldToAllDescendants({ ...child, [field]: value }, field, value)),
  }
}

function updateProject(node: CheckDraft, project: string, includeChildren: boolean): CheckDraft {
  const updated = { ...node, project, product: "", outcomeLookup: "", qualityLookup: "" }
  if (!includeChildren) return updated
  return { ...updated, checks: node.checks.map((child) => updateProject(child, project, true)) }
}

function checkFormReducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case "SET_STRING_FIELD": {
      if (action.path.length > 0 && isCascadeField(action.field)) return state

      if (action.field === "group") {
        return {
          ...state,
          root: updateAtPath(state.root, action.path, (node) =>
            applyFieldToAllDescendants({ ...node, group: action.value }, "group", action.value),
          ),
        }
      }

      if (action.field === "project") {
        const target = getAtPath(state.root, action.path)
        if (target.checks.length > 0) {
          return {
            ...state,
            pendingCascade: { path: action.path, field: "project", value: action.value },
          }
        }
        return {
          ...state,
          root: updateAtPath(state.root, action.path, (node) => updateProject(node, action.value, false)),
        }
      }

      if (isCascadeField(action.field) && getAtPath(state.root, action.path).checks.length > 0) {
        return {
          ...state,
          pendingCascade: { path: action.path, field: action.field, value: action.value },
        }
      }
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({ ...node, [action.field]: action.value })),
      }
    }

    case "SET_IGNORE_OUTCOME":
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({ ...node, ignoreOutcome: action.value })),
      }

    case "CONFIRM_CASCADE": {
      if (state.pendingCascade == null) return state
      const { path, field, value } = state.pendingCascade
      return {
        ...state,
        pendingCascade: null,
        root: updateAtPath(state.root, path, (node) =>
          field === "project"
            ? updateProject(node, value, true)
            : applyFieldToAllDescendants({ ...node, [field]: value }, field, value),
        ),
      }
    }

    case "CANCEL_CASCADE": {
      if (state.pendingCascade == null) return state
      return {
        ...state,
        pendingCascade: null,
      }
    }

    case "ADD_SUBCHECK": {
      const parent = getAtPath(state.root, action.path)
      if (action.path.length !== 0 || parent.level !== "1") return state
      const newChild = emptyCheckDraft({
        project: parent.project,
        product: parent.product,
        group: parent.group,
        level: "2",
        outcomeLookup: parent.outcomeLookup,
        qualityLookup: parent.qualityLookup,
      })
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({ ...node, checks: [...node.checks, newChild] })),
      }
    }

    case "REMOVE_SUBCHECK": {
      const parentPath = action.path.slice(0, -1)
      const index = action.path[action.path.length - 1]
      return {
        ...state,
        root: updateAtPath(state.root, parentPath, (node) => ({
          ...node,
          checks: node.checks.filter((_, i) => i !== index),
        })),
      }
    }

    case "ADD_ROOT_CAUSE":
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({
          ...node,
          rootCauses: [...node.rootCauses, { id: crypto.randomUUID(), name: "", value: "" }],
        })),
      }

    case "SET_ROOT_CAUSE_NAME":
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({
          ...node,
          rootCauses: node.rootCauses.map((rc, i) =>
            i === action.index ? { ...rc, name: action.name, value: action.value } : rc,
          ),
        })),
      }

    case "SET_ROOT_CAUSE_VALUE":
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({
          ...node,
          rootCauses: node.rootCauses.map((rc, i) => (i === action.index ? { ...rc, value: action.value } : rc)),
        })),
      }

    case "REMOVE_ROOT_CAUSE":
      return {
        ...state,
        root: updateAtPath(state.root, action.path, (node) => ({
          ...node,
          rootCauses: node.rootCauses.filter((_, i) => i !== action.index),
        })),
      }

    default:
      return state
  }
}

interface ActionErrors {
  errors: Array<APIError & { id: string }>
}

function getFormString(formData: FormData, key: string): string {
  const value = formData.get(key)
  return typeof value === "string" ? value : ""
}

async function action({
  request,
  params: _params,
}: {
  request: Request
  params: { reference?: string }
}): Promise<ActionErrors | Response> {
  const formData = await request.formData()
  const organisation = getFormString(formData, "organisation")
  const payloadRaw = getFormString(formData, "payload")
  const payload: unknown = payloadRaw.length > 0 ? JSON.parse(payloadRaw) : {}

  if (organisation.length === 0) {
    const result: ActionErrors = {
      errors: [
        {
          propertyName: "organisation",
          id: crypto.randomUUID(),
          errorMessage: "Organisation is required",
          attemptedValue: "",
          customState: null,
          severity: "error",
          errorCode: "NotEmptyValidator",
          formattedMessagePlaceholderValues: {},
        },
      ],
    }
    return result
  }

  try {
    await fetchAPI(`/api/unityReview/Checks/${organisation}`, {
      method: "POST",
      body: payload as Record<string, unknown>,
    }).then(throwIfNotOk)

    return redirect(`/applications/Unity Review/checks`)
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const failures = Array.isArray(error.body) ? (error.body as APIError[]) : []
      if (failures.length > 0) {
        return { errors: failures.map((failure) => ({ ...failure, id: crypto.randomUUID() })) }
      }

      const result: ActionErrors = {
        errors: [
          {
            propertyName: "",
            id: crypto.randomUUID(),
            errorMessage: `Something went wrong while saving. Please try again.`,
            attemptedValue: "",
            customState: null,
            severity: "error",
            errorCode: "ServerError",
            formattedMessagePlaceholderValues: {},
          },
        ],
      }
      return result
    }
    throw error
  }
}

interface CheckFormLoaderData {
  initialDraft: CheckDraft | null
  organisation: string
}

function resolveOrganisation(request: Request): string {
  const url = new URL(request.url)
  const organisationFromUrl = url.searchParams.get("organisation")

  const sessionData = sessionStorage.getItem("checks-filter")
  const filters =
    sessionData !== null && sessionData.length > 0
      ? (qs.parse(sessionData) as unknown as {
          parsed?: { organisation?: string }
          raw?: { organisation?: string }
        })
      : {}

  const candidates: Array<string | null | undefined> = [
    organisationFromUrl,
    filters.parsed?.organisation,
    filters.raw?.organisation,
    getOrganisations()[0],
  ]
  const organisation = candidates.find((value): value is string => value != null && value.length > 0)
  if (organisation == null) {
    throw new Error("No organisation selected")
  }
  return organisation
}

async function loader({ params, request }: LoaderFunctionArgs): Promise<CheckFormLoaderData> {
  const organisation = resolveOrganisation(request)
  const reference = params.reference

  if (reference == null || reference.length === 0) {
    return { initialDraft: null, organisation }
  }

  const check = await fetchAPI<Check>(`/api/unityReview/Checks/${organisation}/${reference}`).then(handleResponse)

  return { initialDraft: fromApiCheck(check), organisation }
}

function useQuestions(organisation: string) {
  const [questions, setQuestions] = useState<QuestionOption[]>([])
  const [isLoading, setIsLoading] = useState(() => organisation.length > 0)

  useEffect(() => {
    if (organisation.length === 0) return

    let cancelled = false
    setIsLoading(true)

    fetchAPI<QuestionOption[]>(`/api/unityReview/Question/${organisation}`)
      .then(handleResponse)
      .then((data) => {
        if (cancelled) return
        setQuestions(data)
      })
      .catch(() => {
        if (!cancelled) setQuestions([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [organisation])

  return { questions, isLoading }
}

function useGuidance(organisation: string) {
  const [guidance, setGuidance] = useState<GuidanceOption[]>([])
  const [isLoading, setIsLoading] = useState(() => organisation.length > 0)

  useEffect(() => {
    if (organisation.length === 0) return

    let cancelled = false
    setIsLoading(true)

    fetchAPI<GuidanceOption[]>(`/api/unityReview/Guidance/${organisation}`)
      .then(handleResponse)
      .then((data) => {
        if (cancelled) return
        setGuidance(data)
      })
      .catch(() => {
        if (!cancelled) setGuidance([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [organisation])

  return { guidance, isLoading }
}

function useRootCauseOptions(organisation: string) {
  const [rootCauseOptions, setRootCauseOptions] = useState<RootCauseOption[]>([])
  const [isLoading, setIsLoading] = useState(() => organisation.length > 0)

  useEffect(() => {
    if (organisation.length === 0) return

    let cancelled = false
    setIsLoading(true)

    fetchAPI<RootCauseOption[]>(`/api/unityReview/RootCauses/${organisation}`)
      .then(handleResponse)
      .then((data) => {
        if (cancelled) return
        setRootCauseOptions(data)
      })
      .catch(() => {
        if (!cancelled) setRootCauseOptions([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [organisation])

  return { rootCauseOptions, isLoading }
}

function useLookupTypes(organisation: string, project: string, projects: string[], cache: Map<string, string[]>) {
  const cacheKey = `${organisation}::${project || `all:${projects.join("|")}`}`
  const [types, setTypes] = useState<string[]>(() => cache.get(cacheKey) ?? [])
  const [isLoading, setIsLoading] = useState(
    () => organisation.length > 0 && (project.length > 0 || projects.length > 0) && !cache.has(cacheKey),
  )

  useEffect(() => {
    if (organisation.length === 0) return

    const cached = cache.get(cacheKey)
    if (cached != null) return

    let cancelled = false
    const projectsToQuery = project.length > 0 ? [project] : projects

    if (projectsToQuery.length === 0) {
      setTypes([])
      setIsLoading(false)
      return
    }

    Promise.all(
      projectsToQuery.map((projectName) =>
        fetchAPI<string[]>(`/api/unityReview/Lookup/types/${organisation}/${projectName}`)
          .then(handleResponse)
          .catch((): string[] => []),
      ),
    )
      .then((projectTypes: string[][]) => {
        if (cancelled) return
        const firstProjectTypes = projectTypes[0] ?? []
        const commonTypes = firstProjectTypes.filter((type) =>
          projectTypes.slice(1).every((types) => types.includes(type)),
        )
        cache.set(cacheKey, commonTypes)
        setTypes(commonTypes)
      })
      .catch(() => {
        if (!cancelled) setTypes([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [organisation, project, projects, cacheKey, cache])

  return { types, isLoading }
}

interface LookupNode {
  id: string
  type: string
  name: string
  children: LookupNode[]
}

interface LookupNodeResponse {
  type: string
  name: string
  children: LookupNodeResponse[]
}

function addLookupNodeIds(nodes: LookupNodeResponse[]): LookupNode[] {
  return nodes.map((node) => ({
    id: crypto.randomUUID(),
    type: node.type,
    name: node.name,
    children: addLookupNodeIds(node.children),
  }))
}

async function fetchLookupPreview(organisation: string, project: string, type: string): Promise<LookupNode[]> {
  const data = await fetchAPI<LookupNodeResponse[]>(
    `/api/unityReview/Lookup/${encodeURIComponent(organisation)}/${encodeURIComponent(project)}/${encodeURIComponent(type)}?parent=`,
  ).then(handleResponse)
  return addLookupNodeIds(data)
}

function useLookupPreview(
  organisation: string,
  project: string,
  type: string,
  enabled: boolean,
  cache: Map<string, LookupNode[]>,
) {
  const cacheKey = `${organisation}::${project}::${type}`
  const [data, setData] = useState<LookupNode[] | null>(() => cache.get(cacheKey) ?? null)
  const [isLoading, setIsLoading] = useState(
    () => enabled && organisation.length > 0 && project.length > 0 && type.length > 0 && !cache.has(cacheKey),
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || organisation.length === 0 || project.length === 0 || type.length === 0) return

    const cached = cache.get(cacheKey)
    if (cached != null) return

    let cancelled = false

    fetchLookupPreview(organisation, project, type)
      .then((result) => {
        if (cancelled) return
        cache.set(cacheKey, result)
        setData(result)
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load lookup values")
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, organisation, project, type, cacheKey, cache])

  return { data, isLoading, error }
}

function PreviewButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-6 shrink-0 gap-1 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      <EyeIcon className="h-3.5 w-3.5" />
      Preview
    </Button>
  )
}

function LookupTree({ nodes, depth = 0 }: { nodes: LookupNode[]; depth?: number }) {
  return (
    <ul className={cn("space-y-1", depth > 0 && "mt-1 ml-4 border-l pl-3")}>
      {nodes.map((n) => (
        <li key={n.id}>
          <span className="text-sm">{n.name}</span>
          {n.type.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({n.type})</span>}
          {n.children.length > 0 && <LookupTree nodes={n.children} depth={depth + 1} />}
        </li>
      ))}
    </ul>
  )
}

function LookupPreviewContent({
  organisation,
  project,
  type,
  cache,
}: {
  organisation: string
  project: string
  type: string
  cache: Map<string, LookupNode[]>
}) {
  const { data, isLoading, error } = useLookupPreview(organisation, project, type, true, cache)

  return (
    <div className="text-sm">
      {isLoading && <p className="text-muted-foreground">Loading lookup values...</p>}
      {error != null && <p className="text-destructive">{error}</p>}
      {!isLoading && error == null && (data?.length ?? 0) === 0 && (
        <p className="text-muted-foreground">No values found for this lookup type.</p>
      )}
      {!isLoading && error == null && data != null && <LookupTree nodes={data} />}
    </div>
  )
}

interface CodedOption {
  name: string
  code: string
  value: string
}

function useFilteredCodedOptions<T extends CodedOption>(items: T[], value: string, searchText: string): T[] {
  return useMemo(() => {
    const withCurrent =
      value.length > 0 && !items.some((i) => i.name === value)
        ? [{ name: value, code: "", value: "" } as T, ...items]
        : items

    const q = searchText.trim().toLowerCase()
    if (q.length === 0) return withCurrent

    const filtered = withCurrent.filter(
      (i) => i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q) || i.value.toLowerCase().includes(q),
    )

    if (value.length > 0 && !filtered.some((i) => i.name === value)) {
      const selected = withCurrent.find((i) => i.name === value)
      if (selected != null) return [selected, ...filtered]
    }

    return filtered
  }, [items, value, searchText])
}

function useFilteredStringOptions(items: string[], value: string, searchText: string): string[] {
  return useMemo(() => {
    const withCurrent = value.length > 0 && !items.includes(value) ? [value, ...items] : items

    const q = searchText.trim().toLowerCase()
    if (q.length === 0) return withCurrent

    const filtered = withCurrent.filter((t) => t.toLowerCase().includes(q))

    if (value.length > 0 && !filtered.includes(value)) {
      return [value, ...filtered]
    }

    return filtered
  }, [items, value, searchText])
}

function LookupSelect({
  label,
  value,
  onChange,
  types,
  isLoading,
  inherited = false,
  required = false,
  onPreview,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  types: string[]
  isLoading: boolean
  inherited?: boolean
  required?: boolean
  onPreview?: () => void
}) {
  const [searchText, setSearchText] = useState("")
  const options = useFilteredStringOptions(types, value, searchText)

  const placeholder = inherited
    ? "Inherited from parent"
    : isLoading
      ? "Loading lookups..."
      : types.length === 0
        ? "No common lookups found"
        : `Select ${label.toLowerCase()}`

  return (
    <div className="relative min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium">
          {label}
          {required ? " *" : ""}
        </label>
        {onPreview != null && <PreviewButton onClick={onPreview} label={`Preview ${label.toLowerCase()} values`} />}
      </div>
      <Select
        value={value}
        onValueChange={onChange}
        disabled={inherited || isLoading}
        onOpenChange={(open) => {
          if (!open) setSearchText("")
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent setSearchText={setSearchText}>
          {options.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No lookups found</div>}
          {options.map((t) => (
            <SelectItem key={t} value={t}>
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ProductSelect({
  organisation,
  project,
  value,
  onChange,
  cache,
  inherited = false,
}: {
  organisation: string
  project: string
  value: string
  onChange: (value: string) => void
  cache: Map<string, LookupNode[]>
  inherited?: boolean
}) {
  const { data, isLoading, error } = useLookupPreview(organisation, project, "products", project.length > 0, cache)

  const products = useMemo(() => {
    const names: string[] = []
    const visit = (nodes: LookupNode[]) => {
      for (const node of nodes) {
        names.push(node.name)
        visit(node.children)
      }
    }
    visit(data ?? [])
    if (value.length > 0 && !names.includes(value)) names.unshift(value)
    return [...new Set(names)]
  }, [data, value])

  const placeholder = inherited
    ? "Inherited from parent"
    : project.length === 0
      ? "Select a project first"
      : isLoading
        ? "Loading products..."
        : error != null
          ? "Products unavailable"
          : products.length === 0
            ? "No products found"
            : "Select product"

  return (
    <div className="relative min-w-0 space-y-1.5">
      <label className="text-sm font-medium">Product</label>
      <Select value={value} onValueChange={onChange} disabled={inherited || project.length === 0 || isLoading}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {products.map((product) => (
            <SelectItem key={product} value={product}>
              {product}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function QuestionSelect({
  value,
  onChange,
  questions,
  isLoading,
  onPreview,
}: {
  value: string
  onChange: (value: string) => void
  questions: QuestionOption[]
  isLoading: boolean
  onPreview?: () => void
}) {
  const [searchText, setSearchText] = useState("")
  const options = useFilteredCodedOptions(questions, value, searchText)

  const placeholder = isLoading
    ? "Loading questions..."
    : questions.length === 0
      ? "No questions found"
      : "Select question"

  return (
    <div className="relative min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium">Question</label>
        {onPreview != null && <PreviewButton onClick={onPreview} label="Preview question" />}
      </div>
      <Select
        value={value}
        onValueChange={onChange}
        disabled={isLoading}
        onOpenChange={(open) => {
          if (!open) setSearchText("")
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder}>{value.length > 0 ? value : undefined}</SelectValue>
        </SelectTrigger>
        <SelectContent
          setSearchText={setSearchText}
          className="w-(--radix-select-trigger-width) max-w-(--radix-select-trigger-width)"
        >
          {options.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No questions found</div>}

          {options.map((q) => (
            <SelectItem key={q.name} value={q.name}>
              <div className="flex max-w-full min-w-0 flex-col">
                <span className="truncate">{q.name}</span>
                {q.value.length > 0 && (
                  <span className="line-clamp-2 text-xs wrap-break-word text-muted-foreground">{q.value}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function GuidanceSelect({
  value,
  onChange,
  guidance,
  isLoading,
  onPreview,
}: {
  value: string
  onChange: (value: string) => void
  guidance: GuidanceOption[]
  isLoading: boolean
  onPreview?: () => void
}) {
  const [searchText, setSearchText] = useState("")
  const options = useFilteredCodedOptions(guidance, value, searchText)

  const placeholder = isLoading
    ? "Loading guidance..."
    : guidance.length === 0
      ? "No guidance found"
      : "Select guidance"

  return (
    <div className="relative min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium">Guidance</label>
        {onPreview != null && <PreviewButton onClick={onPreview} label="Preview guidance" />}
      </div>
      <Select
        value={value}
        onValueChange={onChange}
        disabled={isLoading}
        onOpenChange={(open) => {
          if (!open) setSearchText("")
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder}>{value.length > 0 ? value : undefined}</SelectValue>
        </SelectTrigger>
        <SelectContent
          setSearchText={setSearchText}
          className="w-(--radix-select-trigger-width) max-w-(--radix-select-trigger-width)"
        >
          {options.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No guidance found</div>}

          {options.map((g) => (
            <SelectItem key={g.name} value={g.name}>
              <div className="flex max-w-full min-w-0 flex-col">
                <span className="truncate">{g.name}</span>
                {g.value.length > 0 && (
                  <span className="line-clamp-2 text-xs wrap-break-word text-muted-foreground">{g.value}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function RootCauseNameSelect({
  value,
  onChange,
  options: rootCauseOptions,
  isLoading,
}: {
  value: string
  onChange: (value: string) => void
  options: RootCauseOption[]
  isLoading: boolean
}) {
  const [searchText, setSearchText] = useState("")
  const options = useFilteredCodedOptions(rootCauseOptions, value, searchText)

  const placeholder = isLoading ? "Loading..." : rootCauseOptions.length === 0 ? "No root causes found" : "Select name"

  return (
    <div className="relative">
      <Select
        value={value}
        onValueChange={onChange}
        disabled={isLoading}
        onOpenChange={(open) => {
          if (!open) setSearchText("")
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder}>{value.length > 0 ? value : undefined}</SelectValue>
        </SelectTrigger>
        <SelectContent setSearchText={setSearchText} className="w-(--radix-select-trigger-width)">
          {options.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No root causes found</div>}
          {options.map((o) => (
            <SelectItem key={o.name} value={o.name}>
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{o.name}</span>
                {o.value.length > 0 && (
                  <span className="line-clamp-2 text-xs wrap-break-word text-muted-foreground">{o.value}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function CascadeInput({
  value: externalValue,
  onCommit,
  ...inputProps
}: {
  value: string
  onCommit: (value: string) => void
} & Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "onBlur" | "defaultValue">) {
  const [value, setValue] = useState(externalValue)
  return (
    <Input
      {...inputProps}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value !== externalValue) onCommit(value)
      }}
    />
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{children}</div>
}

function pathsEqual(a: Path, b: Path): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i])
}

function getNodeTitle(node: CheckDraft, depth: number, indexAtDepth: number): string {
  if (node.questionName.length > 0) return node.questionName
  if (node.reference.length > 0) return node.reference
  return depth === 0 ? "New check" : `Sub-check ${indexAtDepth + 1}`
}

function getNodeSubLabel(node: CheckDraft): string {
  return node.reference.length > 0 ? node.reference : "No reference yet"
}

function getBreadcrumbTrail(root: CheckDraft, path: Path): Array<{ id: string; label: string; path: Path }> {
  const trail: Array<{ id: string; label: string; path: Path }> = [
    {
      id: root.id,
      label: getNodeTitle(root, 0, 0),
      path: [],
    },
  ]
  let node = root
  for (let i = 0; i < path.length; i++) {
    const idx = path[i]
    const child = node.checks[idx]
    if (child == null) break
    node = child
    trail.push({ id: node.id, label: getNodeTitle(node, i + 1, idx), path: path.slice(0, i + 1) })
  }
  return trail
}

function remapPathAfterRemoval(selected: Path, removedPath: Path): Path {
  const removedParent = removedPath.slice(0, -1)
  const removedIndex = removedPath[removedPath.length - 1]

  const sharesParent = removedParent.every((v, i) => selected[i] === v) && selected.length > removedParent.length
  if (!sharesParent) return selected

  const wasRemovedOrDescendant = selected.slice(0, removedPath.length).every((v, i) => v === removedPath[i])
  if (wasRemovedOrDescendant) return removedParent

  if (selected[removedParent.length] > removedIndex) {
    const next = [...selected]
    next[removedParent.length] -= 1
    return next
  }

  return selected
}

function TreeRow({
  node,
  path,
  depth,
  selectedPath,
  collapsedIds,
  onToggleCollapse,
  onSelect,
  onRemove,
  onAddSubCheck,
}: {
  node: CheckDraft
  path: Path
  depth: number
  selectedPath: Path
  collapsedIds: Set<string>
  onToggleCollapse: (id: string) => void
  onSelect: (path: Path) => void
  onRemove: (path: Path) => void
  onAddSubCheck: (path: Path) => void
}) {
  const isSelected = pathsEqual(path, selectedPath)
  const hasChildren = node.checks.length > 0
  const isCollapsed = collapsedIds.has(node.id)
  const indexAtDepth = path.length > 0 ? path[path.length - 1] : 0
  const title = getNodeTitle(node, depth, indexAtDepth)
  const subtitle = getNodeSubLabel(node)

  return (
    <div className="min-w-0">
      <div
        className={cn(
          "group flex min-w-0 items-center gap-0.5 rounded-md py-1.5 pr-1.5",
          isSelected ? "bg-primary/10" : "hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
      >
        <button
          type="button"
          disabled={!hasChildren}
          onClick={() => hasChildren && onToggleCollapse(node.id)}
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground",
            hasChildren ? "cursor-pointer" : "invisible cursor-default",
          )}
          aria-label={isCollapsed ? "Expand sub-checks" : "Collapse sub-checks"}
          tabIndex={hasChildren ? 0 : -1}
        >
          <ChevronRightIcon className={cn("h-3.5 w-3.5 transition-transform", !isCollapsed && "rotate-90")} />
        </button>

        <button
          type="button"
          onClick={() => onSelect(path)}
          className="flex min-w-0 flex-1 cursor-pointer flex-col items-start py-0.5 text-left"
        >
          <span
            className={cn("truncate text-sm", isSelected ? "font-semibold text-primary" : "font-medium")}
            title={subtitle}
          >
            {title}
          </span>
          <span className="truncate text-xs text-muted-foreground" title={subtitle}>
            {subtitle}
          </span>
        </button>

        {depth === 0 && node.level === "1" && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-muted-foreground hover:text-primary"
            onClick={(e) => {
              e.stopPropagation()
              onAddSubCheck(path)
            }}
            aria-label={`Add sub-check under ${title}`}
            title={`Add sub-check under ${title}`}
          >
            <PlusIcon className="h-3.5 w-3.5" />
          </Button>
        )}

        {depth > 0 && (
          <Popover>
            <PopoverTrigger
              type="button"
              className={buttonVariants({
                variant: "ghost",
                size: "icon",
                className: "h-6 w-6 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive",
              })}
              aria-label="Remove sub-check"
              title="Remove sub-check"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </PopoverTrigger>
            <PopoverContent side="right" align="start" className="max-w-sm">
              <p className="text-sm text-pretty">
                Remove <span className="font-semibold">&quot;{title}&quot;</span>
                {hasChildren ? " and all its nested sub-checks" : ""}? This cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3 pt-4">
                <PopoverClose type="button" className={buttonVariants({ variant: "ghost" })}>
                  Cancel
                </PopoverClose>
                <PopoverClose
                  type="button"
                  className={buttonVariants({ variant: "destructive" })}
                  onClick={() => onRemove(path)}
                >
                  Confirm
                </PopoverClose>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {hasChildren && !isCollapsed && (
        <div className="min-w-0">
          {node.checks.map((child, i) => (
            <TreeRow
              key={child.id}
              node={child}
              path={[...path, i]}
              depth={depth + 1}
              selectedPath={selectedPath}
              collapsedIds={collapsedIds}
              onToggleCollapse={onToggleCollapse}
              onSelect={onSelect}
              onRemove={onRemove}
              onAddSubCheck={onAddSubCheck}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TextPreviewContent({ code, name, value }: { code: string; name: string; value: string }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">Code:</span> {code || "—"}
        </span>
        <span>
          <span className="font-medium text-foreground">Name:</span> {name}
        </span>
      </div>
      <p className="wrap-break-word whitespace-pre-wrap">{value.length > 0 ? value : "No description provided."}</p>
    </div>
  )
}

const PREVIEW_TITLES: Record<Exclude<PreviewKind, null>, string> = {
  question: "Question preview",
  guidance: "Guidance preview",
  outcomeLookup: "Outcome lookup values",
  qualityLookup: "Quality lookup values",
}

function CheckDetailPanel({
  node,
  path,
  root,
  dispatch,
  projects,
  organisation,
  isEditMode,
  questions,
  questionsLoading,
  guidance,
  guidanceLoading,
  rootCauseOptions,
  rootCauseOptionsLoading,
  lookupTypesCache,
  lookupPreviewCache,
  onSelect,
}: {
  node: CheckDraft
  path: Path
  root: CheckDraft
  dispatch: Dispatch<Action>
  projects: string[]
  organisation: string
  isEditMode: boolean
  questions: QuestionOption[]
  questionsLoading: boolean
  guidance: GuidanceOption[]
  guidanceLoading: boolean
  rootCauseOptions: RootCauseOption[]
  rootCauseOptionsLoading: boolean
  lookupTypesCache: Map<string, string[]>
  lookupPreviewCache: Map<string, LookupNode[]>
  onSelect: (path: Path) => void
}) {
  const depth = path.length
  const referenceReadOnly = isEditMode && node.isExistingReference
  const setField = (field: EditableStringField, value: string) =>
    dispatch({ type: "SET_STRING_FIELD", path, field, value })

  const { types: lookupTypes, isLoading: lookupTypesLoading } = useLookupTypes(
    organisation,
    node.project,
    projects,
    lookupTypesCache,
  )
  const hasProject = node.project.length > 0
  const [previewKind, setPreviewKind] = useState<PreviewKind>(null)

  const selectedQuestion = useMemo(
    () => questions.find((q) => q.name === node.questionName) ?? null,
    [questions, node.questionName],
  )
  const selectedGuidance = useMemo(
    () => guidance.find((g) => g.name === node.guidanceName) ?? null,
    [guidance, node.guidanceName],
  )

  const lookupPreviewType =
    previewKind === "outcomeLookup" ? node.outcomeLookup : previewKind === "qualityLookup" ? node.qualityLookup : ""

  const previewTitle = previewKind != null ? PREVIEW_TITLES[previewKind] : ""

  const breadcrumb = useMemo(() => getBreadcrumbTrail(root, path), [root, path])
  const indexAtDepth = path.length > 0 ? path[path.length - 1] : 0
  const title = getNodeTitle(node, depth, indexAtDepth)

  return (
    <div className="min-w-0 space-y-5">
      <div className="min-w-0 space-y-1.5 border-b pb-3">
        {breadcrumb.length > 1 && (
          <nav className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground" aria-label="Check hierarchy">
            {breadcrumb.map((crumb, i) => (
              <span key={crumb.id} className="flex items-center gap-1">
                {i > 0 && <ChevronRightIcon className="h-3 w-3 shrink-0" />}
                <button
                  type="button"
                  onClick={() => onSelect(crumb.path)}
                  className={cn(
                    "cursor-pointer truncate hover:text-foreground",
                    i === breadcrumb.length - 1 && "font-medium text-foreground",
                  )}
                >
                  {crumb.label}
                </button>
              </span>
            ))}
          </nav>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">{title}</h2>
        </div>
      </div>

      <div className="min-w-0 space-y-3">
        <SectionLabel>Details</SectionLabel>
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium">Reference</label>
            <Input
              value={node.reference}
              onChange={(e) => setField("reference", e.target.value)}
              readOnly={referenceReadOnly}
              disabled={referenceReadOnly}
              placeholder={referenceReadOnly ? undefined : "Leave blank to auto-generate"}
              title={referenceReadOnly ? "Reference is this check's key and can't be changed once created" : undefined}
              className={cn(referenceReadOnly && "bg-muted text-muted-foreground")}
            />
          </div>

          <div className="relative min-w-0 space-y-1.5">
            <label className="text-sm font-medium">Project</label>
            <Select
              value={node.project}
              onValueChange={(val) => setField("project", val)}
              disabled={depth > 0 || projects.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    depth > 0 ? "Inherited from parent" : projects.length > 0 ? "Select project" : "No projects found"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium">Level</label>
            {depth === 0 ? (
              <Select value={node.level} onValueChange={(value) => setField("level", value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose check type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Level 1 - Check with sub-checks</SelectItem>
                  <SelectItem value="3" disabled={node.checks.length > 0}>
                    Level 3 - Standalone check
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input value="Level 2 - Sub-check (leaf)" readOnly disabled />
            )}
            {depth === 0 && node.level === "1" && node.checks.length === 0 && (
              <p className="text-xs text-muted-foreground">Add at least one sub-check to save this level-1 check.</p>
            )}
            {depth === 0 && node.checks.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Level 3 is unavailable while this check has sub-checks. Remove them first to make it standalone.
              </p>
            )}
          </div>
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium">Order</label>
            <Input value={node.order} onChange={(e) => setField("order", e.target.value)} inputMode="numeric" />
          </div>
          <div className="min-w-0 space-y-1.5">
            <ProductSelect
              organisation={organisation}
              project={node.project}
              value={node.product}
              onChange={(value) => setField("product", value)}
              cache={lookupPreviewCache}
              inherited={depth > 0}
            />
          </div>
        </div>
      </div>

      <div className="min-w-0 space-y-3 border-t pt-5">
        <SectionLabel>Question &amp; guidance</SectionLabel>
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          <QuestionSelect
            value={node.questionName}
            onChange={(v) => setField("questionName", v)}
            questions={questions}
            isLoading={questionsLoading}
            onPreview={selectedQuestion != null ? () => setPreviewKind("question") : undefined}
          />

          <GuidanceSelect
            value={node.guidanceName}
            onChange={(v) => setField("guidanceName", v)}
            guidance={guidance}
            isLoading={guidanceLoading}
            onPreview={selectedGuidance != null ? () => setPreviewKind("guidance") : undefined}
          />
        </div>
      </div>

      <div className="min-w-0 space-y-3 border-t pt-5">
        <SectionLabel>Lookup configuration</SectionLabel>
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium">Group / Theme</label>
            {depth > 0 ? (
              <Input
                value={node.group}
                readOnly
                disabled
                title="Sub-checks inherit their group/theme from the parent check"
                className="bg-muted text-muted-foreground"
              />
            ) : (
              <CascadeInput
                key={`${node.id}-group-${node.group}`}
                value={node.group}
                onCommit={(v) => setField("group", v)}
              />
            )}
          </div>

          <LookupSelect
            label="Outcome Lookup"
            value={node.outcomeLookup}
            onChange={(v) => setField("outcomeLookup", v)}
            types={lookupTypes}
            isLoading={lookupTypesLoading}
            inherited={depth > 0}
            onPreview={hasProject && node.outcomeLookup.length > 0 ? () => setPreviewKind("outcomeLookup") : undefined}
          />

          <LookupSelect
            label="Quality Lookup"
            value={node.qualityLookup}
            onChange={(v) => setField("qualityLookup", v)}
            types={lookupTypes}
            isLoading={lookupTypesLoading}
            inherited={depth > 0}
            onPreview={hasProject && node.qualityLookup.length > 0 ? () => setPreviewKind("qualityLookup") : undefined}
          />
        </div>

        <div className="relative flex items-center gap-2 pt-1">
          <Checkbox
            id={`ignoreOutcome-${node.id}`}
            checked={node.ignoreOutcome}
            onCheckedChange={(checked) => dispatch({ type: "SET_IGNORE_OUTCOME", path, value: checked === true })}
          />
          <label htmlFor={`ignoreOutcome-${node.id}`} className="cursor-pointer text-sm leading-none font-medium">
            Ignore outcome
          </label>
        </div>
      </div>

      <div className="min-w-0 space-y-2 border-t pt-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionLabel>Root causes{node.rootCauses.length > 0 ? ` (${node.rootCauses.length})` : ""}</SectionLabel>
          <Button type="button" variant="outline" size="sm" onClick={() => dispatch({ type: "ADD_ROOT_CAUSE", path })}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" />
            Add root cause
          </Button>
        </div>
        {node.rootCauses.length === 0 && (
          <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
            No root causes added.
          </p>
        )}
        {node.rootCauses.map((rc, index) => (
          <div key={rc.id} className="flex items-center gap-2">
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
              <RootCauseNameSelect
                value={rc.name}
                onChange={(v) => {
                  const matched = rootCauseOptions.find((o) => o.name === v)
                  dispatch({
                    type: "SET_ROOT_CAUSE_NAME",
                    path,
                    index,
                    name: v,
                    value: matched?.value ?? "",
                  })
                }}
                options={rootCauseOptions}
                isLoading={rootCauseOptionsLoading}
              />
              <Input
                placeholder="Value"
                value={rc.value}
                readOnly
                disabled
                title="Value is set automatically from the selected root cause name"
                className="min-w-0 bg-muted text-muted-foreground"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-destructive"
              onClick={() => dispatch({ type: "REMOVE_ROOT_CAUSE", path, index })}
              aria-label="Remove root cause"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <Dialog
        open={previewKind != null}
        onOpenChange={(open) => {
          if (!open) setPreviewKind(null)
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{previewTitle}</DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {previewKind === "question" && selectedQuestion != null && (
              <TextPreviewContent
                code={selectedQuestion.code}
                name={selectedQuestion.name}
                value={selectedQuestion.value}
              />
            )}

            {previewKind === "guidance" && selectedGuidance != null && (
              <TextPreviewContent
                code={selectedGuidance.code}
                name={selectedGuidance.name}
                value={selectedGuidance.value}
              />
            )}

            {(previewKind === "outcomeLookup" || previewKind === "qualityLookup") && (
              <LookupPreviewContent
                key={`${organisation}:${node.project}:${lookupPreviewType}`}
                organisation={organisation}
                project={node.project}
                type={lookupPreviewType}
                cache={lookupPreviewCache}
              />
            )}
          </div>

          <div className="flex justify-end border-t pt-3">
            <DialogClose type="button" className={buttonVariants({ variant: "outline" })}>
              Close
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const CASCADE_FIELD_LABEL: Record<CascadeField, string> = {
  project: "project",
  product: "product",
  group: "group",
  outcomeLookup: "outcome lookup",
  qualityLookup: "quality lookup",
}

const SEVERITY_STYLES: Record<string, string> = {
  error: "text-destructive",
  warning: "text-amber-600",
  info: "text-muted-foreground",
}

function ErrorSummary({ errors }: { errors: Array<APIError & { id: string }> }) {
  if (errors.length === 0) return null

  return (
    <ul className="space-y-0.5 text-sm font-medium">
      {errors.map((err) => (
        <li key={err.id} className={SEVERITY_STYLES[err.severity.toLowerCase()] ?? "text-destructive"}>
          {err.propertyName.length > 0 ? `${err.propertyName}: ` : ""}
          {err.errorMessage}
        </li>
      ))}
    </ul>
  )
}

export function CheckForm() {
  const navigate = useNavigate()
  const params = useParams()
  const loaderData = useLoaderData<CheckFormLoaderData | undefined>()
  const checksData = useRouteLoaderData<typeof Checks.loader>(Checks.routeId)
  const organisation = loaderData?.organisation ?? checksData?.loadedOrganisation ?? ""
  const isEditMode = params.reference != null && params.reference.length > 0
  const reference = params.reference

  const initialDraft = useMemo(() => {
    if (!isEditMode || params.reference == null) return null
    if (loaderData?.initialDraft != null) return loaderData.initialDraft
    const fromList = checksData?.checks != null ? findCheckByReference(checksData.checks, params.reference) : null
    return fromList != null ? fromApiCheck(fromList) : null
  }, [isEditMode, checksData?.checks, loaderData?.initialDraft, params.reference])

  if (isEditMode && initialDraft == null) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">
          Could not find check &quot;{reference}&quot; to edit. It may have been removed, or you may need to reload the
          Checks list.
        </p>
        <Button type="button" variant="outline" className="mt-4" onClick={() => void navigate(-1)}>
          Back
        </Button>
      </div>
    )
  }

  return (
    <CheckFormEditor
      key={`${organisation}:${reference ?? "create"}`}
      organisation={organisation}
      isEditMode={isEditMode}
      initialDraft={initialDraft}
    />
  )
}

function CheckFormEditor({
  organisation,
  isEditMode,
  initialDraft,
}: {
  organisation: string
  isEditMode: boolean
  initialDraft: CheckDraft | null
}) {
  const navigation = useNavigation()
  const navigate = useNavigate()
  const actionData = useActionData<ActionErrors | undefined>()
  const projects = useMemo(
    () => (organisation.length > 0 ? getProjectsByOrganisation(organisation) : []),
    [organisation],
  )

  const [state, dispatch] = useReducer(checkFormReducer, initialDraft, (draft) => ({
    root: draft ?? emptyCheckDraft(),
    pendingCascade: null,
  }))

  const [selectedPath, setSelectedPath] = useState<Path>([])
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set())

  const effectiveSelectedPath = getAtPathSafe(state.root, selectedPath) != null ? selectedPath : []
  const selectedNode = getAtPathSafe(state.root, effectiveSelectedPath) ?? state.root

  const handleSelect = (path: Path) => setSelectedPath(path)

  const handleToggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAddSubCheck = (parentPath: Path) => {
    const parent = getAtPathSafe(state.root, parentPath)
    if (parent == null) return
    const newIndex = parent.checks.length
    dispatch({ type: "ADD_SUBCHECK", path: parentPath })
    setCollapsedIds((prev) => {
      if (!prev.has(parent.id)) return prev
      const next = new Set(prev)
      next.delete(parent.id)
      return next
    })
    setSelectedPath([...parentPath, newIndex])
  }

  const handleRemoveSubCheck = (path: Path) => {
    dispatch({ type: "REMOVE_SUBCHECK", path })
    setSelectedPath((prev) => remapPathAfterRemoval(prev, path))
  }

  const { questions, isLoading: questionsLoading } = useQuestions(organisation)
  const { guidance, isLoading: guidanceLoading } = useGuidance(organisation)
  const { rootCauseOptions, isLoading: rootCauseOptionsLoading } = useRootCauseOptions(organisation)
  const lookupTypesCacheRef = useRef<Map<string, string[]>>(new Map())
  const lookupPreviewCacheRef = useRef<Map<string, LookupNode[]>>(new Map())

  const isSubmitting = navigation.state === "submitting"
  const [clientError, setClientError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    const parsed = rootCheckSchema.safeParse(state.root)
    if (!parsed.success) {
      event.preventDefault()
      const firstIssue = parsed.error.issues[0]
      const message = firstIssue?.message ?? "Please check the form for errors"
      setClientError(message)
      return
    }

    const orderConflict = collectOrderConflict(state.root)
    if (orderConflict != null) {
      event.preventDefault()
      setClientError(orderConflict)
      return
    }

    setClientError(null)
    const hidden = event.currentTarget.elements.namedItem("payload") as HTMLInputElement
    hidden.value = JSON.stringify(toSaveRequest(state.root))
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 border-b bg-background px-6 py-4">
        <h1 className="text-base font-semibold">{isEditMode ? "Edit check" : "New check"}</h1>
        <p className="text-sm text-muted-foreground">
          {isEditMode ? `Reference: ${initialDraft?.reference ?? ""}` : "Create New check"}
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden sm:flex-row">
        <aside className="w-full shrink-0 overflow-y-auto border-b bg-muted/10 py-2 sm:h-full sm:w-80 sm:border-r sm:border-b-0 lg:w-96">
          <div className="px-3 pb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Check structure
          </div>
          <div className="px-1">
            <TreeRow
              node={state.root}
              path={[]}
              depth={0}
              selectedPath={effectiveSelectedPath}
              collapsedIds={collapsedIds}
              onToggleCollapse={handleToggleCollapse}
              onSelect={handleSelect}
              onRemove={handleRemoveSubCheck}
              onAddSubCheck={handleAddSubCheck}
            />
          </div>
        </aside>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <Form ref={formRef} method="post" onSubmit={handleSubmit} className="w-full min-w-0">
            <input type="hidden" name="organisation" value={organisation} />
            <input type="hidden" name="payload" />
            <div className="p-6 pb-28">
              <CheckDetailPanel
                key={`${selectedNode.id}:${organisation}:${selectedNode.project}`}
                node={selectedNode}
                path={effectiveSelectedPath}
                root={state.root}
                dispatch={dispatch}
                projects={projects}
                organisation={organisation}
                isEditMode={isEditMode}
                questions={questions}
                questionsLoading={questionsLoading}
                guidance={guidance}
                guidanceLoading={guidanceLoading}
                rootCauseOptions={rootCauseOptions}
                rootCauseOptionsLoading={rootCauseOptionsLoading}
                lookupTypesCache={lookupTypesCacheRef.current}
                lookupPreviewCache={lookupPreviewCacheRef.current}
                onSelect={handleSelect}
              />
            </div>

            <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-background/95 px-6 py-3 shadow-[0_-1px_8px_rgba(0,0,0,0.04)] backdrop-blur supports-backdrop-filter:bg-background/80">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {clientError != null && <p className="text-sm font-medium text-destructive">{clientError}</p>}
                  {clientError == null && actionData?.errors != null && actionData.errors.length > 0 && (
                    <ErrorSummary errors={actionData.errors} />
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      navigate(-1)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : isEditMode ? "Save changes" : "Save check"}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>

      <Dialog
        open={state.pendingCascade != null}
        onOpenChange={(open) => {
          if (!open) dispatch({ type: "CANCEL_CASCADE" })
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply to sub-checks?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-pretty">
            {state.pendingCascade != null &&
              `You changed the ${CASCADE_FIELD_LABEL[state.pendingCascade.field]}. Apply this value to the parent and its sub-checks?${state.pendingCascade.field === "project" ? " Product and lookup values will be cleared because they depend on the project." : ""}`}
          </p>
          <div className="flex items-center justify-end gap-3 pt-4">
            <DialogClose
              type="button"
              className={buttonVariants({ variant: "ghost" })}
              onClick={() => dispatch({ type: "CANCEL_CASCADE" })}
            >
              No, keep current values
            </DialogClose>
            <DialogClose
              type="button"
              className={buttonVariants()}
              onClick={() => dispatch({ type: "CONFIRM_CASCADE" })}
            >
              Yes, apply to all
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

CheckForm.action = action
CheckForm.loader = loader
